import './team-roster.styles.scss';
import { Link } from 'react-router-dom';
import { positions } from '@/utils/positions';
import { levels } from '@/utils/levels';
import { formatMoney } from '@shared/contracts.js';
import SortTh from '@/components/SHARED/sort-th/sort-th.component';
import { useSortable } from '@/utils/useSortable';

// A team's roster with season averages.
// hidden: admin tools only. The league's private traits for this team
// ({players: {id: {chemistry, potential, leadership}}}); managers never
// receive these. playerLink(teamId, playerId): optional path for each
// player's page.
const TeamRoster = ({ team, hidden, playerLink }) => {

    const sorter = useSortable();
    if (!team) return null;

    const avg = (s, key) => (s?.gp ? s[key] / s.gp : null);
    const hiddenOf = (p, key) => hidden?.players?.[p.id]?.[key] ?? null;
    const players = sorter.apply(Object.values(team.players)
        .sort((a, b) => a.pos1 - b.pos1 || b.overall - a.overall), {
        name: p => p.lName, pos: p => p.pos1, klass: p => p.year ?? p.age, ht: p => p.height,
        ovr: p => p.overall, off: p => p.off_quality, def: p => p.def_quality, sta: p => p.stamina,
        salary: p => p.contract?.salary ?? null, years: p => p.contract?.years ?? null,
        chem: p => hiddenOf(p, 'chemistry'), pot: p => hiddenOf(p, 'potential'), lead: p => hiddenOf(p, 'leadership'),
        gp: p => p.season?.gp ?? 0, mpg: p => avg(p.season, 'min'), ppg: p => avg(p.season, 'pts'),
        rpg: p => (p.season?.gp ? (p.season.oreb + p.season.dreb) / p.season.gp : null),
        apg: p => avg(p.season, 'ast'), fg: p => (p.season?.fga ? p.season.fgm / p.season.fga : null),
    });
    const th = (k, label, first, className = 'num', title) =>
        <SortTh k={k} label={label} sorter={sorter} first={first} className={className} title={title} />;
    const starters = new Set(positions.map(p => team.depth?.[`${p.id}_0`]));
    // Pro players are under contract.
    const contracts = players.some(p => p.contract);

    const per = (s, key) => (s?.gp ? (s[key] / s.gp).toFixed(1) : '-');
    const reb = (s) => (s?.gp ? ((s.oreb + s.dreb) / s.gp).toFixed(1) : '-');
    const pct = (made, att) => (att ? `${Math.round(made / att * 100)}%` : '-');
    const height = (inches) => `${Math.floor(inches / 12)}'${inches % 12}"`;
    const klass = (p) => (p.year ? `${levels[p.year - 1].short}${p.redshirt ? ' (RS)' : p.fifthYear ? ' (5th)' : ''}` : p.age);

    return (
        <div className='table-scroll team-roster'>
            <table className='g-table roster-table'>
                <thead>
                    <tr>
                        {th('name', 'Player', 'asc', '')}
                        {th('pos', 'Pos', 'asc', '')}
                        {th('klass', players[0]?.year ? 'Yr' : 'Age', 'desc', '')}
                        {th('ht', 'Ht')}
                        {th('ovr', 'Ovr')}
                        {th('off', 'Off')}
                        {th('def', 'Def')}
                        {th('sta', 'Sta')}
                        {contracts && th('salary', 'Salary')}
                        {contracts && th('years', 'Yrs', 'desc', 'num', 'Years left on the contract, this season included')}
                        {hidden && th('chem', 'Chem', 'desc', 'num hidden-col', 'Hidden from managers')}
                        {hidden && th('pot', 'Pot', 'desc', 'num hidden-col', 'Hidden from managers')}
                        {hidden && th('lead', 'Lead', 'desc', 'num hidden-col', 'Hidden from managers')}
                        {th('gp', 'GP')}
                        {th('mpg', 'MPG')}
                        {th('ppg', 'PPG')}
                        {th('rpg', 'RPG')}
                        {th('apg', 'APG')}
                        {th('fg', 'FG%')}
                    </tr>
                </thead>
                <tbody>
                    {players.map(p => {
                        const s = p.season;
                        const h = hidden?.players?.[p.id];
                        return (
                            <tr key={p.id} className={p.injury ? 'injured' : ''}>
                                <td>
                                    {playerLink ?
                                        <Link to={playerLink(team.id, p.id)} className={starters.has(p.id) ? 'starter' : ''}>{p.fName} {p.lName}</Link> :
                                        <span className={starters.has(p.id) ? 'starter' : ''}>{p.fName} {p.lName}</span>
                                    }
                                    {p.injury && <span className='injury-tag'> Out {p.injury.gamesOut}</span>}
                                </td>
                                <td>{positions[p.pos1 - 1].short}</td>
                                <td>{klass(p)}</td>
                                <td className='num'>{height(p.height)}</td>
                                <td className='num strong'>{p.overall}</td>
                                <td className='num'>{p.off_quality}</td>
                                <td className='num'>{p.def_quality}</td>
                                <td className='num'>{p.stamina}</td>
                                {contracts && <td className='num'>{p.contract ? formatMoney(p.contract.salary) : '-'}</td>}
                                {contracts && <td className='num'>{p.contract ? (p.contract.years || 'Exp') : '-'}</td>}
                                {hidden && <td className='num hidden-col'>{h?.chemistry ?? '-'}</td>}
                                {hidden && <td className='num hidden-col'>{h?.potential ?? '-'}</td>}
                                {hidden && <td className='num hidden-col'>{h?.leadership ?? '-'}</td>}
                                <td className='num'>{s?.gp ?? 0}</td>
                                <td className='num'>{per(s, 'min')}</td>
                                <td className='num'>{per(s, 'pts')}</td>
                                <td className='num'>{reb(s)}</td>
                                <td className='num'>{per(s, 'ast')}</td>
                                <td className='num'>{pct(s?.fgm, s?.fga)}</td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

export default TeamRoster
